// Procedural vector art for buildings and units, with drop shadows,
// construction scaffolds, hp bars, selection rings.
// All draw fns expect ctx translated to the entity's world center
// (world units: hex circumradius = HEX_SIZE = 24). Owner: client-render.

import { BUILDINGS, UNITS, isBoxOrder, siteBuildTime } from '../../shared/constants'
import { DIRS } from '../../shared/hex'
import type { Building, BuildingType, ItemType, UnitWire } from '../../shared/types'
import { unitMaxHp } from '../../shared/person'
import { buildingMaxHp, townHallPhase, type TownHallPhase } from '../../shared/townhall'
import { rngFor } from '../../shared/rng'
import { HEX_SIZE } from '../state'
import { hexPathAt, hexToPixel, rgb, css, shade, mixRgb, type RGB } from './terrain'
import { ITEM_PATHS, ITEM_LEAN_DEG, type ItemInk } from '../ui/itemIcons'
import { treeGrowthStage } from './treeGrowth'

const S = HEX_SIZE
const TAU = Math.PI * 2

// ---- shared bits ---------------------------------------------------------------

function shadow(ctx: CanvasRenderingContext2D, rx: number, ry: number, dy: number): void {
  ctx.fillStyle = 'rgba(10,14,10,0.28)'
  ctx.beginPath()
  ctx.ellipse(0, dy, rx, ry, 0, 0, TAU)
  ctx.fill()
}

function box(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, c: string): void {
  ctx.fillStyle = c
  ctx.fillRect(x, y, w, h)
}

/** Gabled roof spanning [x0,x1] with eave line at y and ridge height h. */
function roof(ctx: CanvasRenderingContext2D, x0: number, x1: number, y: number, h: number, cL: string, cR: string): void {
  const mx = (x0 + x1) / 2
  ctx.fillStyle = cL
  ctx.beginPath()
  ctx.moveTo(x0 - 1, y)
  ctx.lineTo(mx, y - h)
  ctx.lineTo(mx, y)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = cR
  ctx.beginPath()
  ctx.moveTo(mx, y - h)
  ctx.lineTo(x1 + 1, y)
  ctx.lineTo(mx, y)
  ctx.closePath()
  ctx.fill()
}

function flag(ctx: CanvasRenderingContext2D, x: number, y: number, h: number, color: string, sway: number): void {
  ctx.strokeStyle = '#4a3826'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(x, y)
  ctx.lineTo(x, y - h)
  ctx.stroke()
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.moveTo(x, y - h)
  ctx.quadraticCurveTo(x + 3.5, y - h + 0.6 + sway, x + 6, y - h + 1.4 + sway)
  ctx.lineTo(x + 1.2, y - h + 3.6)
  ctx.closePath()
  ctx.fill()
}

// ---- item glyphs (vector, shared geometry with the DOM SVG icons) ---------------

/** Canvas ink per material, tuned to read on any terrain. */
const ITEM_INK: Record<ItemInk, string> = {
  wood: '#8a6a42',
  steel: '#cfd4da',
  string: '#e8e2d0',
}

const itemPath2d = new Map<string, Path2D>()

/** Draw an item as the same leaning vector silhouette the UI icons use,
 *  centered on the current origin, `size` px tall. Used for ground drops and
 *  the armory gear-swap flight — anywhere an item exists outside a hand. */
export function drawItemGlyph(ctx: CanvasRenderingContext2D, item: ItemType, size: number): void {
  ctx.save()
  ctx.rotate((ITEM_LEAN_DEG * Math.PI) / 180)
  ctx.scale(size / 24, size / 24)
  ctx.translate(-12, -12) // geometry lives in a 24×24 box
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  for (const seg of ITEM_PATHS[item]) {
    let path = itemPath2d.get(seg.d)
    if (!path) {
      path = new Path2D(seg.d)
      itemPath2d.set(seg.d, path)
    }
    if (seg.fill) {
      ctx.fillStyle = ITEM_INK[seg.ink]
      ctx.fill(path)
      ctx.strokeStyle = 'rgba(30,26,18,0.55)' // thin dark rim so steel reads on light ground
      ctx.lineWidth = 0.8
      ctx.stroke(path)
    } else {
      ctx.strokeStyle = ITEM_INK[seg.ink]
      ctx.lineWidth = seg.w ?? 1.8
      ctx.stroke(path)
    }
  }
  ctx.restore()
}

function smooth01(a: number, b: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)))
  return t * t * (3 - 2 * t)
}

/** The quick gear-swap flourish at the town-hall armory: the person's old item is
 *  tossed toward the hall while the new one is handed out to them, crossing midway.
 *  Absolute world-space coords; p runs 0→1 over the swap. */
export function drawGearSwap(
  ctx: CanvasRenderingContext2D,
  from: ItemType | null,
  to: ItemType | null,
  sx: number,
  sy: number,
  hx: number,
  hy: number,
  p: number,
): void {
  ctx.save()
  const hop = -S * 0.6
  const size = S * 0.8
  if (from) {
    // old item: person → hall, fading as it lands on the rack
    const x = sx + (hx - sx) * p
    const y = sy + (hy - sy) * p + Math.sin(p * Math.PI) * hop
    ctx.globalAlpha = 1 - smooth01(0.55, 1, p)
    ctx.save()
    ctx.translate(x, y)
    drawItemGlyph(ctx, from, size)
    ctx.restore()
  }
  if (to) {
    // new item: hall → person, fading in as they take hold of it
    const x = hx + (sx - hx) * p
    const y = hy + (sy - hy) * p + Math.sin(p * Math.PI) * hop
    ctx.globalAlpha = smooth01(0, 0.45, p)
    ctx.save()
    ctx.translate(x, y)
    drawItemGlyph(ctx, to, size)
    ctx.restore()
  }
  ctx.globalAlpha = 1
  ctx.restore()
}

export function drawHpBar(ctx: CanvasRenderingContext2D, y: number, frac: number, w = S * 0.9): void {
  const f = Math.max(0, Math.min(1, frac))
  ctx.fillStyle = 'rgba(12,12,14,0.75)'
  ctx.fillRect(-w / 2 - 0.5, y - 0.5, w + 1, 3)
  ctx.fillStyle = f > 0.55 ? '#59c14f' : f > 0.25 ? '#e0b73c' : '#e6553a'
  ctx.fillRect(-w / 2, y, w * f, 2)
}

export function drawSelectionRing(ctx: CanvasRenderingContext2D, rx: number, ry: number, color: string): void {
  ctx.save()
  ctx.shadowColor = color
  ctx.shadowBlur = 7
  ctx.strokeStyle = 'rgba(255,255,244,0.95)'
  ctx.lineWidth = 1.4
  ctx.beginPath()
  ctx.ellipse(0, ry * 0.35, rx, ry, 0, 0, TAU)
  ctx.stroke()
  ctx.restore()
}

// ---- buildings ------------------------------------------------------------------

/** World-angle from an oriented building's anchor to its second footprint cell
 *  (the pier's water deck) — how far to rotate its art. hexToPixel is linear, so
 *  feeding it the DIRS offset directly yields the pixel delta between the cells. */
export function pierArtAngle(dir: number | undefined): number {
  const d = DIRS[(((dir ?? 0) % 6) + 6) % 6]
  const p = hexToPixel(d.q, d.r, S)
  return Math.atan2(p.y, p.x)
}

/** Full building draw (art only; hp/progress overlays via drawBuildingOverlay).
 *  `prep` — the site is still being cleared/levelled by its crew: draw a see-through
 *  image of the planned building over the untouched land instead of a works site. */
export function drawBuilding(ctx: CanvasRenderingContext2D, b: Building, color: string, now: number, groundSquash = 1, prep = false, tick = 0): void {
  if (b.type === 'plant') return bSapling(ctx, b.done ? treeGrowthStage(b, tick) : -1, now, b.id)
  if (!b.done) return drawSite(ctx, b, color, now, groundSquash, prep)
  drawBuildingArt(ctx, b.type, color, now, b.id, b.type === 'pier' || b.type === 'ramp' ? pierArtAngle(b.dir) : 0, groundSquash, b.hallPhase)
}

function drawBuildingArt(ctx: CanvasRenderingContext2D, type: BuildingType, color: string, now: number, seed: number, angle = 0, groundSquash = 1, phase?: TownHallPhase): void {
  const rand = rngFor(seed, 101)
  const sway = Math.sin(now * 0.0022 + seed) * 0.9
  switch (type) {
    case 'townhall': return bTownhall(ctx, color, sway, townHallPhase(phase))
    case 'house': return bHouse(ctx, color, rand)
    case 'farm': return bFarm(ctx, color, rand, groundSquash)
    case 'chop': return bChop(ctx)
    case 'plant': return bSapling(ctx, 0, now, seed)
    case 'quarry': return bQuarry(ctx, rand)
    case 'mine': return bMine(ctx, rand)
    case 'storehouse': return bStorehouse(ctx, color, rand)
    case 'workbench': return bWorkbench(ctx, color)
    case 'dummy': return bDummy(ctx, sway)
    case 'palisade': return bPalisade(ctx)
    case 'pier': return bPier(ctx, sway, angle)
    case 'ramp': return bRamp(ctx, angle)
    case 'barracks': return bBarracks(ctx, color, sway, rand)
    case 'tower': return bTower(ctx, color, sway)
    case 'camp': return bCamp(ctx, now, rand)
    case 'road': return // finished roads are terrain; only sites render
  }
}

/** These silhouettes share the same seven-hex plot and service corners. The
 * renderer already scales town-hall art by 1.7, so the campsite itself stays
 * small while the later buildings grow into the reserved grounds. */
function bTownhall(ctx: CanvasRenderingContext2D, color: string, sway: number, phase: TownHallPhase): void {
  shadow(ctx, S * (phase <= 2 ? 0.48 : 0.79), S * 0.25, S * 0.35)
  if (phase === 1) {
    hallTent(ctx, -S * 0.09, S * 0.13, S * 0.64, S * 0.5, '#c8b894', '#9b8868', color)
    hallBedroll(ctx, -S * 0.17, S * 0.27, '#829291')
    hallBedroll(ctx, S * 0.04, S * 0.31, '#ba9d77')
    hallFire(ctx, S * 0.34, S * 0.2, sway)
  } else if (phase === 2) {
    hallTent(ctx, -S * 0.22, S * 0.08, S * 0.83, S * 0.61, '#d4c49b', '#ac9670', color)
    hallTent(ctx, S * 0.45, S * 0.19, S * 0.43, S * 0.35, '#ac9e77', '#837553', color)
    hallBedroll(ctx, -S * 0.15, S * 0.28, '#8d9d94')
    hallFire(ctx, S * 0.2, S * 0.34, sway)
    for (let i = 0; i < 3; i++) {
      box(ctx, -S * 0.66, S * (0.24 + i * 0.048), S * 0.3, S * 0.04, '#795934')
      box(ctx, -S * 0.66, S * (0.24 + i * 0.048), S * 0.036, S * 0.04, '#bfa374')
    }
    flag(ctx, -S * 0.45, -S * 0.22, S * 0.53, color, sway)
  } else if (phase === 3 || phase === 4) {
    if (phase === 4) {
      box(ctx, -S * 0.68, S * 0.24, S * 1.36, S * 0.18, '#827e72')
      for (const side of [-1, 1]) {
        box(ctx, side * S * 0.65 - S * 0.1, -S * 0.16, S * 0.2, S * 0.57, '#795b37')
        roof(ctx, side * S * 0.65 - S * 0.13, side * S * 0.65 + S * 0.13, -S * 0.16, S * 0.22, '#807343', '#625732')
      }
    }
    box(ctx, -S * 0.56, S * 0.24, S * 1.12, S * 0.12, phase === 4 ? '#96907d' : '#765631')
    box(ctx, -S * 0.52, -S * 0.29, S * 1.04, S * 0.54, '#b18c55')
    for (let i = -2; i <= 2; i++) box(ctx, i * S * 0.245 - S * 0.022, -S * 0.3, S * 0.044, S * 0.57, '#5f442a')
    box(ctx, -S * 0.54, -S * 0.02, S * 1.08, S * 0.038, '#715032')
    roof(ctx, -S * 0.65, S * 0.65, -S * 0.3, S * 0.53, '#c5a053', '#94733a')
    for (const side of [-1, 1]) {
      box(ctx, side * S * 0.34 - S * 0.07, -S * 0.19, S * 0.14, S * 0.17, '#584528')
      box(ctx, side * S * 0.34 - S * 0.046, -S * 0.17, S * 0.092, S * 0.12, '#ead38d')
    }
    box(ctx, -S * 0.105, S * 0.035, S * 0.21, S * 0.28, '#51391f')
    box(ctx, -S * 0.12, S * 0.012, S * 0.24, S * 0.035, color)
    flag(ctx, S * 0.04, -S * 0.82, S * (phase === 4 ? 0.46 : 0.29), color, sway)
    if (phase === 4) {
      for (const side of [-1, 1]) for (let i = 0; i < 4; i++) {
        const x = side * S * (0.3 + i * 0.115)
        box(ctx, x - S * 0.034, S * 0.18, S * 0.068, S * 0.32, '#775534')
        roof(ctx, x - S * 0.034, x + S * 0.034, S * 0.18, S * 0.08, '#a3814f', '#6b4e30')
      }
      for (const side of [-1, 1]) box(ctx, side < 0 ? -S * 0.71 : S * 0.28, S * 0.38, S * 0.43, S * 0.034, '#4e3925')
    }
  } else {
    box(ctx, -S * 0.71, S * 0.25, S * 1.42, S * 0.15, '#757d7b')
    box(ctx, -S * 0.55, -S * 0.79, S * 1.1, S * 1.04, '#a2aaa4')
    for (let row = 0; row < 6; row++) {
      box(ctx, -S * 0.55, S * (-0.7 + row * 0.15), S * 1.1, S * 0.018, '#858f89')
      for (let col = 0; col < 4; col++) box(ctx, S * (-0.48 + col * 0.27 + (row % 2) * 0.1), S * (-0.79 + row * 0.15), S * 0.013, S * 0.09, '#8f9992')
    }
    box(ctx, -S * 0.6, -S * 0.8, S * 1.2, S * 0.09, '#737e79')
    for (let i = -2; i <= 2; i++) box(ctx, i * S * 0.24 - S * 0.075, -S * 0.96, S * 0.15, S * 0.2, '#abb3aa')
    for (const side of [-1, 1]) {
      const x = side * S * 0.56
      box(ctx, x - S * 0.14, -S * 0.91, S * 0.28, S * 1.23, '#919c97')
      box(ctx, x - S * 0.17, -S * 0.97, S * 0.34, S * 0.08, '#68766f')
      for (let i = -1; i <= 1; i++) box(ctx, x + i * S * 0.11 - S * 0.04, -S * 1.12, S * 0.08, S * 0.2, '#b0b9ad')
      box(ctx, x - S * 0.025, -S * 0.6, S * 0.05, S * 0.18, '#39443f')
      box(ctx, x - S * 0.06, -S * 0.33, S * 0.12, S * 0.28, color)
    }
    box(ctx, -S * 0.14, -S * 0.02, S * 0.28, S * 0.34, '#393b31')
    for (const x of [-0.075, 0, 0.075]) box(ctx, S * x, -S * 0.02, S * 0.017, S * 0.32, '#69756a')
    flag(ctx, 0, -S * 0.97, S * 0.52, color, sway)
  }
  // Persistent services remain in the west/east/south sectors across every phase.
  const storeX = -S * 1.03
  box(ctx, storeX - S * 0.24, S * 0.11, S * 0.48, S * 0.07, '#806442')
  if (phase >= 2) {
    for (const side of [-1, 1]) box(ctx, storeX + side * S * 0.21, -S * 0.23, S * 0.035, S * 0.36, '#6a4d31')
    roof(ctx, storeX - S * 0.25, storeX + S * 0.25, -S * 0.24, S * 0.12, phase === 2 ? '#ad9c76' : '#8d7549', '#6c593d')
  }
  const benchX = S * 1.03
  for (const side of [-1, 1]) box(ctx, benchX + side * S * 0.175 - S * 0.02, S * 0.04, S * 0.04, S * 0.2, '#66482c')
  box(ctx, benchX - S * 0.22, 0, S * 0.44, S * 0.055, '#b39462')
  box(ctx, benchX + S * 0.06, -S * 0.04, S * 0.09, S * 0.038, '#755536')
  for (const x of [0.19, 0.59]) box(ctx, S * x, S * 0.16, S * 0.032, S * 0.38, '#674a2e')
  box(ctx, S * 0.19, S * 0.22, S * 0.432, S * 0.035, '#705031')
  box(ctx, S * 0.19, S * 0.39, S * 0.432, S * 0.03, '#705031')
}

function hallTent(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, light: string, dark: string, color: string): void {
  ctx.fillStyle = light
  ctx.beginPath(); ctx.moveTo(x - width / 2, y); ctx.lineTo(x, y - height); ctx.lineTo(x + width * 0.18, y); ctx.closePath(); ctx.fill()
  ctx.fillStyle = dark
  ctx.beginPath(); ctx.moveTo(x, y - height); ctx.lineTo(x + width / 2, y); ctx.lineTo(x + width * 0.18, y); ctx.closePath(); ctx.fill()
  ctx.fillStyle = '#493c2b'
  ctx.beginPath(); ctx.moveTo(x - width * 0.12, y); ctx.lineTo(x + width * 0.04, y - height * 0.6); ctx.lineTo(x + width * 0.18, y); ctx.closePath(); ctx.fill()
  box(ctx, x - width * 0.025, y - height - S * 0.03, width * 0.05, S * 0.07, '#715338')
  box(ctx, x + width * 0.04, y - height * 0.78, width * 0.1, height * 0.13, color)
}
function hallBedroll(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
  box(ctx, x, y, S * 0.13, S * 0.2, color)
  box(ctx, x, y, S * 0.13, S * 0.055, '#d1c4a3')
}
function hallFire(ctx: CanvasRenderingContext2D, x: number, y: number, sway: number): void {
  ctx.fillStyle = '#6f7264'
  ctx.beginPath(); ctx.ellipse(x, y, S * 0.13, S * 0.07, 0, 0, TAU); ctx.fill()
  ctx.fillStyle = '#d97b36'
  ctx.beginPath(); ctx.moveTo(x - S * 0.065, y); ctx.lineTo(x + S * 0.015, y - S * (0.17 + sway * 0.014)); ctx.lineTo(x + S * 0.072, y); ctx.closePath(); ctx.fill()
  ctx.fillStyle = '#efc163'
  ctx.beginPath(); ctx.moveTo(x - S * 0.033, y); ctx.lineTo(x + S * 0.014, y - S * 0.1); ctx.lineTo(x + S * 0.04, y); ctx.closePath(); ctx.fill()
}

function bHouse(ctx: CanvasRenderingContext2D, color: string, rand: () => number): void {
  shadow(ctx, S * 0.48, S * 0.22, S * 0.36)
  const tone = 0.92 + rand() * 0.16
  box(ctx, -S * 0.4, -S * 0.12, S * 0.8, S * 0.48, css(shade(rgb('#9b7a52'), tone)))
  roof(ctx, -S * 0.48, S * 0.48, -S * 0.1, S * 0.42, '#c9a24e', '#a37f38') // thatch
  ctx.strokeStyle = 'rgba(120,92,40,0.7)'
  ctx.lineWidth = 0.8
  for (let i = 0; i < 3; i++) {
    ctx.beginPath()
    ctx.moveTo(-S * 0.34 + i * S * 0.14, -S * 0.16 - i * S * 0.05)
    ctx.lineTo(-S * 0.1 + i * S * 0.14, -S * 0.34 - i * S * 0.02)
    ctx.stroke()
  }
  ctx.fillStyle = '#4a3520'
  box(ctx, -S * 0.09, S * 0.08, S * 0.18, S * 0.28, '#4a3520')
  ctx.fillStyle = '#e8d089'
  box(ctx, S * 0.16, -S * 0.02, S * 0.12, S * 0.12, '#e8d089')
  ctx.fillStyle = color // door trim in faction color
  ctx.fillRect(-S * 0.11, S * 0.06, S * 0.22, S * 0.035)
}

/** One cultivated hex, draped independently onto its terrain tile. */
export function drawFarmField(ctx: CanvasRenderingContext2D, seed: number, groundSquash = 1): void {
  const rand = rngFor(seed, 101)
  ctx.save()
  ctx.scale(1, groundSquash)
  ctx.save()
  ctx.beginPath()
  hexPathAt(ctx, 0, 0, S * .96)
  ctx.clip()
  ctx.fillStyle = '#77523a'
  ctx.fillRect(-S, -S, S * 2, S * 2)
  ctx.strokeStyle = '#aa8151'
  ctx.lineWidth = S * .04
  ctx.beginPath()
  hexPathAt(ctx, 0, 0, S * .925)
  ctx.stroke()
  for (let row = 0; row < 7; row++) {
    const y = (-.69 + row * .23) * S
    ctx.fillStyle = row % 2 ? '#66432d' : '#6b4930'
    ctx.fillRect(-S, y, S * 2, S * .125)
    ctx.fillStyle = '#977048'
    ctx.fillRect(-S, y + S * .12, S * 2, S * .023)
  }
  ctx.fillStyle = '#9c7950'
  ctx.beginPath()
  ctx.moveTo(-S * .06, S * .22)
  ctx.lineTo(S * .06, S * .22)
  ctx.lineTo(S * .13, S)
  ctx.lineTo(-S * .13, S)
  ctx.closePath()
  ctx.fill()
  ctx.lineCap = 'round'
  for (let row = 0; row < 7; row++) {
    const y = (-.64 + row * .23) * S
    for (let col = -3; col <= 3; col++) {
      const x = (col * .23 + (rand() - .5) * .055) * S
      const py = y + (rand() - .5) * S * .035
      const height = S * (.10 + rand() * .07)
      const ripe = (row !== 1 && row !== 5) || rand() > .6
      if (Math.abs(x) < S * .13 && py > S * .20) continue
      ctx.strokeStyle = ripe ? '#ae9b43' : '#648341'
      ctx.lineWidth = S * .023
      ctx.beginPath()
      ctx.moveTo(x, py + S * .04)
      ctx.lineTo(x, py - height)
      ctx.moveTo(x, py)
      ctx.lineTo(x - S * .055, py - height * .65)
      ctx.moveTo(x, py + S * .015)
      ctx.lineTo(x + S * .05, py - height * .72)
      ctx.stroke()
      ctx.strokeStyle = ripe ? (col % 2 ? '#e2bd62' : '#f0d17c') : '#8daa56'
      ctx.lineWidth = S * .047
      ctx.beginPath()
      ctx.moveTo(x, py - height)
      ctx.lineTo(x, py - height + S * .04)
      ctx.moveTo(x - S * .055, py - height * .65)
      ctx.lineTo(x - S * .04, py - height * .45)
      ctx.moveTo(x + S * .05, py - height * .72)
      ctx.lineTo(x + S * .035, py - height * .50)
      ctx.stroke()
    }
  }
  ctx.restore()
  const posts = [[-.67, -.43], [-.34, -.66], [.04, -.66], [.42, -.66], [.67, -.43]]
  ctx.lineCap = 'round'
  ctx.strokeStyle = '#a6895b'
  ctx.lineWidth = S * .029
  for (const rail of [.04, .115]) {
    ctx.beginPath()
    posts.forEach(([x, y], i) => {
      if (i === 0) ctx.moveTo(x * S, (y - rail) * S)
      else ctx.lineTo(x * S, (y - rail) * S)
    })
    ctx.stroke()
  }
  for (const [x, y] of posts) {
    ctx.strokeStyle = '#60432b'
    ctx.lineWidth = S * .044
    ctx.beginPath()
    ctx.moveTo(x * S, y * S)
    ctx.lineTo(x * S, (y - .16) * S)
    ctx.stroke()
    ctx.fillStyle = '#c3a36d'
    ctx.fillRect((x - .016) * S, (y - .17) * S, S * .032, S * .035)
  }
  ctx.restore()
}

function bFarm(ctx: CanvasRenderingContext2D, color: string, _rand: () => number, _groundSquash = 1): void {
  ctx.save()
  ctx.translate(0, -S * .10)
  shadow(ctx, S * .27, S * .11, S * .16)
  box(ctx, -S * .17, -S * .055, S * .34, S * .18, '#785334')
  box(ctx, -S * .135, -S * .03, S * .27, S * .16, '#493b28')
  box(ctx, -S * .21, S * .13, S * .42, S * .035, '#a08353')
  box(ctx, -S * .055, S * .035, S * .15, S * .085, '#c3a24c')
  box(ctx, -S * .05, S * .035, S * .14, S * .018, '#e1c575')
  box(ctx, S * .01, S * .035, S * .018, S * .085, '#907039')
  for (const x of [-.185, .15]) {
    box(ctx, x * S, -S * .12, S * .035, S * .27, '#715132')
    box(ctx, x * S, -S * .12, S * .012, S * .27, '#b18a54')
  }
  roof(ctx, -S * .25, S * .25, -S * .085, S * .19, '#c7a55c', '#947744')
  ctx.strokeStyle = '#e1c276'
  ctx.lineWidth = S * .015
  ctx.beginPath()
  ctx.moveTo(-S * .25, -S * .085)
  ctx.lineTo(0, -S * .275)
  ctx.lineTo(S * .25, -S * .085)
  ctx.stroke()
  box(ctx, -S * .22, -S * .083, S * .44, S * .027, '#715132')
  box(ctx, S * .12, -S * .055, S * .05, S * .075, color)
  box(ctx, S * .235, S * .065, S * .095, S * .10, '#967144')
  ctx.fillStyle = '#d3b564'
  ctx.beginPath()
  ctx.ellipse(S * .282, S * .067, S * .049, S * .021, 0, 0, TAU)
  ctx.fill()
  box(ctx, S * .235, S * .108, S * .095, S * .014, '#5e4931')
  ctx.restore()
}

/** A low woodcutter's spot at the BASE of a still-standing tree: a felled log with
 *  an axe struck in and a couple of chips. Kept small so the tree above stays read. */
function bChop(ctx: CanvasRenderingContext2D): void {
  // a felled log lying near the trunk
  ctx.fillStyle = '#7a5c38'
  ctx.beginPath()
  ctx.ellipse(-S * 0.16, S * 0.16, S * 0.16, S * 0.055, 0.15, 0, TAU)
  ctx.fill()
  ctx.fillStyle = '#c8a870' // pale cut end
  ctx.beginPath()
  ctx.ellipse(-S * 0.31, S * 0.14, S * 0.04, S * 0.05, 0.15, 0, TAU)
  ctx.fill()
  // axe struck into the log, handle raking up
  ctx.strokeStyle = '#8a6a42'
  ctx.lineWidth = Math.max(1.4, S * 0.04)
  ctx.beginPath()
  ctx.moveTo(S * 0.02, S * 0.12); ctx.lineTo(S * 0.2, -S * 0.14)
  ctx.stroke()
  ctx.fillStyle = '#aab2b8'
  ctx.beginPath()
  ctx.moveTo(S * 0.16, -S * 0.2); ctx.lineTo(S * 0.28, -S * 0.13); ctx.lineTo(S * 0.16, -S * 0.08)
  ctx.closePath(); ctx.fill()
  // a couple of fresh chips
  ctx.fillStyle = '#b98f5a'
  ctx.beginPath(); ctx.ellipse(S * 0.12, S * 0.2, S * 0.045, S * 0.028, 0.4, 0, TAU); ctx.fill()
  ctx.beginPath(); ctx.ellipse(S * 0.24, S * 0.16, S * 0.04, S * 0.022, -0.3, 0, TAU); ctx.fill()
}

/** A hand-planted shoot, then a branching sapling; no building pad or scaffold. */
function bSapling(ctx: CanvasRenderingContext2D, stage: number, now: number, seed: number): void {
  ctx.fillStyle = '#725033'
  ctx.beginPath(); ctx.ellipse(0, S * 0.12, S * 0.24, S * 0.12, 0, 0, TAU); ctx.fill()
  ctx.fillStyle = '#a9844c'
  ctx.beginPath(); ctx.ellipse(-S * 0.09, S * 0.11, S * 0.055, S * 0.027, -0.5, 0, TAU); ctx.fill()
  if (stage < 0) return // queued planting is just a prepared patch and sprout badge
  const h = S * [0.54, 0.94, 1.42][stage]
  const sway = Math.sin(now * 0.0015 + seed) * S * 0.018
  ctx.strokeStyle = stage === 0 ? '#799849' : '#81603b'
  ctx.lineWidth = S * (stage === 0 ? 0.046 : 0.065)
  ctx.lineCap = 'round'
  ctx.beginPath(); ctx.moveTo(0, S * 0.1); ctx.quadraticCurveTo(-S * 0.04, -h * 0.4, sway, -h); ctx.stroke()
  for (let i = 0; i < 3 + stage * 2; i++) {
    const side = i % 2 ? 1 : -1
    const y = -h * (0.27 + i / (3 + stage * 2) * 0.65)
    const x = side * S * (0.12 + stage * 0.07) + sway
    ctx.strokeStyle = '#85974a'
    ctx.lineWidth = S * 0.03
    ctx.beginPath(); ctx.moveTo(sway * 0.4, y + S * 0.1); ctx.lineTo(x, y - S * 0.04); ctx.stroke()
    ctx.fillStyle = i % 3 === 0 ? '#b5d977' : i % 2 ? '#76b058' : '#55944d'
    const w = S * (0.12 + stage * 0.065)
    ctx.beginPath(); ctx.moveTo(x - w, y); ctx.lineTo(x - w * 0.25, y - w * 0.9); ctx.lineTo(x + w, y - w * 0.3); ctx.lineTo(x + w * 0.6, y + w * 0.5); ctx.closePath(); ctx.fill()
  }
}

/** A small "work here" marker for a terraform order (dig / fill / fillsand): a low
 *  mound of earth (sand for fillsand) with a shovel stood in it. The floating status
 *  badge is the real indicator, so this stays deliberately small. */
function bTerra(ctx: CanvasRenderingContext2D, type: BuildingType): void {
  const earth = type === 'fillsand' ? '#d8c48e' : '#8a6a42'
  ctx.fillStyle = earth
  ctx.beginPath(); ctx.ellipse(-S * 0.04, S * 0.14, S * 0.17, S * 0.075, 0, 0, TAU); ctx.fill()
  ctx.fillStyle = type === 'fillsand' ? '#c9b489' : '#6e5334'
  ctx.beginPath(); ctx.ellipse(S * 0.06, S * 0.1, S * 0.09, S * 0.045, 0, 0, TAU); ctx.fill()
  // shovel: shaft + blade
  ctx.strokeStyle = '#8a6a42'
  ctx.lineWidth = Math.max(1.4, S * 0.045)
  ctx.beginPath(); ctx.moveTo(-S * 0.14, S * 0.1); ctx.lineTo(S * 0.02, -S * 0.34); ctx.stroke()
  ctx.fillStyle = '#aab2b8'
  ctx.beginPath()
  ctx.moveTo(-S * 0.2, S * 0.12); ctx.lineTo(-S * 0.08, S * 0.16); ctx.lineTo(-S * 0.14, S * 0.02)
  ctx.closePath(); ctx.fill()
}

function bQuarry(ctx: CanvasRenderingContext2D, rand: () => number): void {
  shadow(ctx, S * 0.6, S * 0.26, S * 0.4)
  // pit
  ctx.fillStyle = '#6e6a5c'
  ctx.beginPath()
  ctx.ellipse(0, S * 0.15, S * 0.5, S * 0.26, 0, 0, TAU)
  ctx.fill()
  ctx.fillStyle = '#57544a'
  ctx.beginPath()
  ctx.ellipse(S * 0.05, S * 0.18, S * 0.32, S * 0.15, 0, 0, TAU)
  ctx.fill()
  // cut stone blocks: cube illusion (top light, front mid, side dark)
  const block = (x: number, y: number, s: number) => {
    ctx.fillStyle = '#c6c2b4'
    ctx.beginPath()
    ctx.moveTo(x, y - s * 0.5); ctx.lineTo(x + s, y - s * 0.85); ctx.lineTo(x + s * 2, y - s * 0.5); ctx.lineTo(x + s, y - s * 0.15)
    ctx.closePath(); ctx.fill()
    ctx.fillStyle = '#a29e90'
    ctx.fillRect(x, y - s * 0.5, s, s)
    ctx.fillStyle = '#7c7869'
    ctx.beginPath()
    ctx.moveTo(x + s, y - s * 0.15); ctx.lineTo(x + s * 2, y - s * 0.5); ctx.lineTo(x + s * 2, y + s * 0.5); ctx.lineTo(x + s, y + s * 0.85)
    ctx.closePath(); ctx.fill()
  }
  block(-S * 0.55, -S * 0.15, S * 0.16)
  block(-S * 0.28, -S * 0.32, S * 0.14)
  block(S * 0.18, -S * 0.3, S * 0.17)
  // chisel + sledge hint
  ctx.strokeStyle = '#4e453a'
  ctx.lineWidth = 1.1
  ctx.beginPath()
  ctx.moveTo(-S * 0.05, S * 0.05); ctx.lineTo(S * 0.1, -S * 0.08)
  ctx.stroke()
  void rand
}

function bMine(ctx: CanvasRenderingContext2D, rand: () => number): void {
  shadow(ctx, S * 0.58, S * 0.25, S * 0.42)
  // rocky mound
  ctx.fillStyle = '#7f7a72'
  ctx.beginPath()
  ctx.moveTo(-S * 0.6, S * 0.3)
  ctx.quadraticCurveTo(-S * 0.45, -S * 0.42, 0, -S * 0.48)
  ctx.quadraticCurveTo(S * 0.5, -S * 0.4, S * 0.6, S * 0.3)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = '#938e86'
  ctx.beginPath()
  ctx.moveTo(-S * 0.4, S * 0.3)
  ctx.quadraticCurveTo(-S * 0.3, -S * 0.3, 0, -S * 0.4)
  ctx.quadraticCurveTo(S * 0.1, -S * 0.35, S * 0.12, -S * 0.1)
  ctx.lineTo(S * 0.05, S * 0.3)
  ctx.closePath()
  ctx.fill()
  // timbered adit (dark arch, post-and-lintel frame)
  ctx.fillStyle = '#191410'
  ctx.beginPath()
  ctx.moveTo(-S * 0.18, S * 0.3)
  ctx.lineTo(-S * 0.18, S * 0.02)
  ctx.arc(0, S * 0.02, S * 0.18, Math.PI, 0)
  ctx.lineTo(S * 0.18, S * 0.3)
  ctx.closePath()
  ctx.fill()
  ctx.strokeStyle = '#6e5433'
  ctx.lineWidth = 2.2
  ctx.beginPath()
  ctx.moveTo(-S * 0.21, S * 0.3); ctx.lineTo(-S * 0.21, -S * 0.02)
  ctx.moveTo(S * 0.21, S * 0.3); ctx.lineTo(S * 0.21, -S * 0.02)
  ctx.moveTo(-S * 0.27, -S * 0.02); ctx.lineTo(S * 0.27, -S * 0.02)
  ctx.stroke()
  // rail + ore cart
  ctx.strokeStyle = '#54483a'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(0, S * 0.32); ctx.lineTo(S * 0.62, S * 0.44)
  ctx.moveTo(0, S * 0.38); ctx.lineTo(S * 0.58, S * 0.52)
  ctx.stroke()
  ctx.save()
  ctx.translate(S * 0.38, S * 0.3)
  box(ctx, -S * 0.12, -S * 0.12, S * 0.24, S * 0.14, '#5e4a30')
  ctx.fillStyle = '#8e939b' // ore lumps
  for (let i = 0; i < 3; i++) {
    ctx.beginPath()
    ctx.arc(-S * 0.06 + i * S * 0.06, -S * 0.12, S * 0.035, 0, TAU)
    ctx.fill()
  }
  ctx.fillStyle = '#2e2620'
  ctx.beginPath(); ctx.arc(-S * 0.07, S * 0.04, S * 0.04, 0, TAU); ctx.fill()
  ctx.beginPath(); ctx.arc(S * 0.07, S * 0.04, S * 0.04, 0, TAU); ctx.fill()
  ctx.restore()
  void rand
}

/** A plank walkway on pilings reaching from the shore out over the water.
 *  The art is drawn at the footprint centroid; `angle` is the world direction
 *  from the land anchor to the water deck (pierArtAngle), so the walkway runs
 *  along that axis with the land end at -0.87S and the water end at +0.87S.
 *  Pilings and the mooring post stay screen-upright as depth cues — only the
 *  deck itself rotates, so no orientation ever reads upside-down. */
function bPier(ctx: CanvasRenderingContext2D, sway: number, angle: number): void {
  const ux = Math.cos(angle)
  const uy = Math.sin(angle)
  // shadow hugging the walkway
  ctx.fillStyle = 'rgba(10,14,10,0.28)'
  ctx.beginPath()
  ctx.ellipse(0, S * 0.18, S * 0.95, S * 0.3, angle, 0, TAU)
  ctx.fill()
  // pilings into the water — on the WATER half (+t along the axis), upright
  ctx.strokeStyle = '#5a4426'
  ctx.lineWidth = 3
  for (const t of [0.12, 0.48, 0.82]) {
    const px = ux * S * t
    const py = uy * S * t
    ctx.beginPath()
    ctx.moveTo(px, py - S * 0.06)
    ctx.lineTo(px, py + S * 0.3)
    ctx.stroke()
  }
  // the deck: a long plank walkway along the anchor→water axis
  ctx.save()
  ctx.rotate(angle)
  ctx.fillStyle = '#9a7c4f'
  ctx.fillRect(-S * 0.87, -S * 0.17, S * 1.74, S * 0.34)
  ctx.strokeStyle = '#6b5638'
  ctx.lineWidth = 0.9
  for (let i = 1; i < 6; i++) { // plank seams across the walkway
    const x = -S * 0.87 + i * S * 0.29
    ctx.beginPath()
    ctx.moveTo(x, -S * 0.17)
    ctx.lineTo(x, S * 0.17)
    ctx.stroke()
  }
  ctx.strokeStyle = '#7c6540' // deck rim
  ctx.lineWidth = 1
  ctx.strokeRect(-S * 0.87, -S * 0.17, S * 1.74, S * 0.34)
  ctx.restore()
  // mooring post at the water end + rope back to the deck, swaying gently
  const mx = ux * S * 0.8
  const my = uy * S * 0.8
  ctx.strokeStyle = '#4a3a24'
  ctx.lineWidth = 2.6
  ctx.beginPath()
  ctx.moveTo(mx, my + S * 0.1)
  ctx.lineTo(mx, my - S * 0.5)
  ctx.stroke()
  ctx.strokeStyle = '#3d2f1c'
  ctx.lineWidth = 1.2
  const dx = ux * S * 0.28
  const dy = uy * S * 0.28
  ctx.beginPath()
  ctx.moveTo(mx, my - S * 0.4)
  ctx.quadraticCurveTo((mx + dx) / 2 + sway * 0.02 * S, (my - S * 0.4 + dy) / 2, dx, dy)
  ctx.stroke()
}

/** A timber incline climbing the cliff toward its facing (dir). Drawn over the
 *  baked cliff line so it reads as the one crossable spot in the drop. */
function bRamp(ctx: CanvasRenderingContext2D, angle: number): void {
  const ux = Math.cos(angle)
  const uy = Math.sin(angle)
  // shadow pooled along the ramp axis
  ctx.fillStyle = 'rgba(10,14,10,0.3)'
  ctx.beginPath()
  ctx.ellipse(ux * S * 0.28, uy * S * 0.28 + S * 0.12, S * 0.9, S * 0.34, angle, 0, TAU)
  ctx.fill()
  ctx.save()
  ctx.rotate(angle) // +x now points up the cliff
  const x0 = -S * 0.5 // base, on the low side
  const x1 = S * 0.98 // lip, just past the shared edge (covers the cliff line)
  const wb = S * 0.66 // wide at the base
  const wt = S * 0.44 // narrower at the top, faking the climb
  ctx.fillStyle = '#8a6a3f' // timber deck
  ctx.beginPath()
  ctx.moveTo(x0, -wb / 2)
  ctx.lineTo(x1, -wt / 2)
  ctx.lineTo(x1, wt / 2)
  ctx.lineTo(x0, wb / 2)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = '#6e5231' // top lip the ramp delivers onto
  ctx.beginPath()
  ctx.moveTo(x1, -wt / 2)
  ctx.lineTo(x1 + S * 0.08, -wt / 2)
  ctx.lineTo(x1 + S * 0.08, wt / 2)
  ctx.lineTo(x1, wt / 2)
  ctx.closePath()
  ctx.fill()
  ctx.strokeStyle = 'rgba(58,40,20,0.72)' // plank rungs across the incline
  ctx.lineWidth = 1.6
  for (let i = 1; i < 6; i++) {
    const f = i / 6
    const x = x0 + (x1 - x0) * f
    const w = (wb + (wt - wb) * f) / 2
    ctx.beginPath()
    ctx.moveTo(x, -w)
    ctx.lineTo(x, w)
    ctx.stroke()
  }
  ctx.strokeStyle = '#5c4326' // side rails
  ctx.lineWidth = 2.2
  ctx.beginPath(); ctx.moveTo(x0, -wb / 2); ctx.lineTo(x1, -wt / 2); ctx.stroke()
  ctx.beginPath(); ctx.moveTo(x0, wb / 2); ctx.lineTo(x1, wt / 2); ctx.stroke()
  ctx.restore()
}

/** A ring of sharpened stakes filling the hex — reads as a wall segment. */
function bPalisade(ctx: CanvasRenderingContext2D): void {
  shadow(ctx, S * 0.7, S * 0.3, S * 0.3)
  ctx.strokeStyle = '#5a4426'
  ctx.lineWidth = 1
  for (let i = 0; i < 7; i++) {
    const x = -S * 0.66 + i * S * 0.22
    const h = S * (0.55 + ((i * 37) % 10) * 0.02)
    ctx.fillStyle = i % 2 ? '#8a6d44' : '#7a5f3a'
    ctx.beginPath()
    ctx.moveTo(x - S * 0.09, S * 0.3)
    ctx.lineTo(x - S * 0.09, -h + S * 0.14)
    ctx.lineTo(x, -h)
    ctx.lineTo(x + S * 0.09, -h + S * 0.14)
    ctx.lineTo(x + S * 0.09, S * 0.3)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
  }
  // lashing rope
  ctx.strokeStyle = '#3d2f1c'
  ctx.lineWidth = 1.4
  ctx.beginPath()
  ctx.moveTo(-S * 0.72, -S * 0.1)
  ctx.lineTo(S * 0.72, -S * 0.1)
  ctx.stroke()
}

/** A straw training dummy on a post, arms out on a crossbar, wobbling when hit. */
function bDummy(ctx: CanvasRenderingContext2D, sway: number): void {
  shadow(ctx, S * 0.4, S * 0.18, S * 0.28)
  ctx.save()
  ctx.rotate(sway * 0.04)
  // post + crossbar
  ctx.strokeStyle = '#6b5638'
  ctx.lineWidth = 2.2
  ctx.beginPath()
  ctx.moveTo(0, S * 0.34); ctx.lineTo(0, -S * 0.5)
  ctx.moveTo(-S * 0.4, -S * 0.18); ctx.lineTo(S * 0.4, -S * 0.18)
  ctx.stroke()
  // straw body
  ctx.fillStyle = '#c9a967'
  ctx.beginPath()
  ctx.ellipse(0, -S * 0.05, S * 0.24, S * 0.34, 0, 0, TAU)
  ctx.fill()
  ctx.strokeStyle = '#a8854a'
  ctx.lineWidth = 0.8
  ctx.beginPath()
  ctx.moveTo(-S * 0.18, -S * 0.2); ctx.lineTo(S * 0.14, S * 0.16)
  ctx.moveTo(S * 0.18, -S * 0.16); ctx.lineTo(-S * 0.12, S * 0.2)
  ctx.stroke()
  // straw head with target patch
  ctx.fillStyle = '#d4b877'
  ctx.beginPath()
  ctx.arc(0, -S * 0.62, S * 0.18, 0, TAU)
  ctx.fill()
  ctx.strokeStyle = '#8e3b2c'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.arc(0, -S * 0.05, S * 0.09, 0, TAU)
  ctx.stroke()
  ctx.restore()
}

/** Open-air crafting bench: sturdy table, a lean-to canopy, tools on top. */
function bWorkbench(ctx: CanvasRenderingContext2D, color: string): void {
  shadow(ctx, S * 0.55, S * 0.24, S * 0.34)
  // two posts holding a small slanted canopy
  ctx.strokeStyle = '#6b5638'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(-S * 0.42, S * 0.3); ctx.lineTo(-S * 0.42, -S * 0.38)
  ctx.moveTo(S * 0.42, S * 0.3); ctx.lineTo(S * 0.42, -S * 0.24)
  ctx.stroke()
  ctx.fillStyle = '#8f9aa6'
  ctx.beginPath()
  ctx.moveTo(-S * 0.52, -S * 0.38)
  ctx.lineTo(S * 0.52, -S * 0.24)
  ctx.lineTo(S * 0.44, -S * 0.1)
  ctx.lineTo(-S * 0.6, -S * 0.24)
  ctx.closePath()
  ctx.fill()
  // the bench: thick top on stout legs
  ctx.strokeStyle = '#5a4426'
  ctx.lineWidth = 1.6
  ctx.beginPath()
  ctx.moveTo(-S * 0.3, S * 0.34); ctx.lineTo(-S * 0.3, S * 0.1)
  ctx.moveTo(S * 0.3, S * 0.34); ctx.lineTo(S * 0.3, S * 0.1)
  ctx.stroke()
  box(ctx, -S * 0.42, S * 0.0, S * 0.84, S * 0.14, '#9a7c4f')
  ctx.fillStyle = '#7c6238'
  ctx.fillRect(-S * 0.42, S * 0.0, S * 0.84, S * 0.035)
  // tools on the top: a saw blade + mallet head
  ctx.fillStyle = '#c3c8cc'
  ctx.fillRect(-S * 0.26, -S * 0.07, S * 0.3, S * 0.05)
  ctx.fillStyle = '#6b5638'
  ctx.fillRect(S * 0.1, -S * 0.12, S * 0.09, S * 0.11)
  // faction pennant on the tall post
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.moveTo(-S * 0.42, -S * 0.38)
  ctx.lineTo(-S * 0.16, -S * 0.31)
  ctx.lineTo(-S * 0.42, -S * 0.26)
  ctx.closePath()
  ctx.fill()
}

function bStorehouse(ctx: CanvasRenderingContext2D, color: string, rand: () => number): void {
  shadow(ctx, S * 0.65, S * 0.27, S * 0.4)
  // long warehouse
  box(ctx, -S * 0.6, -S * 0.14, S * 1.0, S * 0.5, '#8a7350')
  ctx.strokeStyle = '#63512f'
  ctx.lineWidth = 0.9
  for (let i = 0; i < 4; i++) {
    ctx.beginPath()
    ctx.moveTo(-S * 0.55 + i * S * 0.25, -S * 0.14)
    ctx.lineTo(-S * 0.55 + i * S * 0.25, S * 0.36)
    ctx.stroke()
  }
  roof(ctx, -S * 0.68, S * 0.48, -S * 0.12, S * 0.4, '#7f8894', '#5d6570')
  // wide door
  ctx.fillStyle = '#4a3822'
  box(ctx, -S * 0.32, S * 0.04, S * 0.3, S * 0.32, '#4a3822')
  ctx.strokeStyle = '#2e2114'
  ctx.beginPath()
  ctx.moveTo(-S * 0.32, S * 0.04); ctx.lineTo(-S * 0.02, S * 0.36)
  ctx.moveTo(-S * 0.02, S * 0.04); ctx.lineTo(-S * 0.32, S * 0.36)
  ctx.stroke()
  ctx.fillStyle = color
  ctx.fillRect(-S * 0.6, -S * 0.14, S * 1.0, S * 0.035)
  // crate stack beside
  const crate = (x: number, y: number, s: number) => {
    box(ctx, x, y, s, s, '#b08d58')
    ctx.strokeStyle = '#7c5f36'
    ctx.lineWidth = 0.8
    ctx.strokeRect(x, y, s, s)
    ctx.beginPath()
    ctx.moveTo(x, y); ctx.lineTo(x + s, y + s)
    ctx.stroke()
  }
  crate(S * 0.44, S * 0.14, S * 0.22)
  crate(S * 0.62, S * 0.2, S * 0.17)
  crate(S * 0.52, -S * 0.06, S * 0.19)
  void rand
}

function bBarracks(ctx: CanvasRenderingContext2D, color: string, sway: number, rand: () => number): void {
  shadow(ctx, S * 0.62, S * 0.26, S * 0.42)
  // sturdy stone-footed hall
  box(ctx, -S * 0.55, S * 0.18, S * 1.1, S * 0.26, '#7b7468')
  box(ctx, -S * 0.5, -S * 0.22, S * 1.0, S * 0.42, '#6d5136')
  roof(ctx, -S * 0.58, S * 0.58, -S * 0.2, S * 0.4, '#5f6a75', '#454e58')
  ctx.fillStyle = '#38281a'
  box(ctx, -S * 0.1, S * 0.1, S * 0.2, S * 0.34, '#38281a')
  ctx.fillStyle = '#e6d290'
  box(ctx, -S * 0.38, -S * 0.1, S * 0.11, S * 0.13, '#e6d290')
  box(ctx, S * 0.27, -S * 0.1, S * 0.11, S * 0.13, '#e6d290')
  // twin banners
  flag(ctx, -S * 0.52, S * 0.18, S * 0.62, color, sway)
  flag(ctx, S * 0.52, S * 0.18, S * 0.62, color, -sway)
  // weapon rack: leaning spears
  ctx.strokeStyle = '#8a6b42'
  ctx.lineWidth = 1.1
  for (let i = 0; i < 3; i++) {
    const x = S * 0.62 + i * S * 0.07
    ctx.beginPath()
    ctx.moveTo(x, S * 0.42)
    ctx.lineTo(x - S * 0.1, -S * 0.05 - i * 2)
    ctx.stroke()
    ctx.fillStyle = '#b9c0c6'
    ctx.beginPath()
    ctx.moveTo(x - S * 0.1, -S * 0.05 - i * 2)
    ctx.lineTo(x - S * 0.14, -S * 0.14 - i * 2)
    ctx.lineTo(x - S * 0.06, -S * 0.12 - i * 2)
    ctx.closePath()
    ctx.fill()
  }
  void rand
}

function bTower(ctx: CanvasRenderingContext2D, color: string, sway: number): void {
  shadow(ctx, S * 0.42, S * 0.2, S * 0.46)
  // tapered stone shaft
  ctx.fillStyle = '#8d8880'
  ctx.beginPath()
  ctx.moveTo(-S * 0.3, S * 0.46)
  ctx.lineTo(-S * 0.2, -S * 0.62)
  ctx.lineTo(S * 0.2, -S * 0.62)
  ctx.lineTo(S * 0.3, S * 0.46)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = '#6e6a62' // shadow side
  ctx.beginPath()
  ctx.moveTo(S * 0.06, S * 0.46)
  ctx.lineTo(S * 0.05, -S * 0.62)
  ctx.lineTo(S * 0.2, -S * 0.62)
  ctx.lineTo(S * 0.3, S * 0.46)
  ctx.closePath()
  ctx.fill()
  // masonry
  ctx.strokeStyle = 'rgba(70,66,60,0.55)'
  ctx.lineWidth = 0.7
  for (let i = 0; i < 5; i++) {
    const y = S * 0.34 - i * S * 0.2
    const w = 0.3 - i * 0.018
    ctx.beginPath()
    ctx.moveTo(-S * w, y)
    ctx.lineTo(S * w, y)
    ctx.stroke()
  }
  // crenellated top
  ctx.fillStyle = '#9b968e'
  box(ctx, -S * 0.28, -S * 0.74, S * 0.56, S * 0.14, '#9b968e')
  for (let i = 0; i < 4; i++)
    box(ctx, -S * 0.28 + i * S * 0.16, -S * 0.84, S * 0.09, S * 0.1, '#9b968e')
  // arrow slit + doorway
  ctx.fillStyle = '#241d14'
  box(ctx, -S * 0.025, -S * 0.45, S * 0.05, S * 0.2, '#241d14')
  box(ctx, -S * 0.08, S * 0.22, S * 0.16, S * 0.24, '#241d14')
  flag(ctx, S * 0.2, -S * 0.84, S * 0.3, color, sway)
}

function bCamp(ctx: CanvasRenderingContext2D, now: number, rand: () => number): void {
  shadow(ctx, S * 0.62, S * 0.26, S * 0.42)
  // two dark tents
  const tent = (x: number, y: number, w: number, h: number) => {
    ctx.fillStyle = '#3c3430'
    ctx.beginPath()
    ctx.moveTo(x - w, y); ctx.lineTo(x, y - h); ctx.lineTo(x + w, y)
    ctx.closePath(); ctx.fill()
    ctx.fillStyle = '#524740'
    ctx.beginPath()
    ctx.moveTo(x - w, y); ctx.lineTo(x, y - h); ctx.lineTo(x - w * 0.15, y)
    ctx.closePath(); ctx.fill()
    ctx.fillStyle = '#1c1714'
    ctx.beginPath()
    ctx.moveTo(x - w * 0.22, y); ctx.lineTo(x, y - h * 0.55); ctx.lineTo(x + w * 0.22, y)
    ctx.closePath(); ctx.fill()
  }
  tent(-S * 0.34, S * 0.3, S * 0.3, S * 0.42)
  tent(S * 0.4, S * 0.24, S * 0.26, S * 0.36)
  // fire pit with flicker
  const fl = 0.75 + 0.25 * Math.sin(now * 0.013) * Math.sin(now * 0.007 + 2)
  ctx.fillStyle = '#3a2c20'
  ctx.beginPath()
  ctx.ellipse(S * 0.02, S * 0.42, S * 0.14, S * 0.06, 0, 0, TAU)
  ctx.fill()
  ctx.fillStyle = `rgba(255,150,40,${0.85 * fl})`
  ctx.beginPath()
  ctx.moveTo(-S * 0.06, S * 0.42)
  ctx.quadraticCurveTo(-S * 0.02, S * (0.28 - 0.04 * fl), S * 0.02, S * (0.24 - 0.06 * fl))
  ctx.quadraticCurveTo(S * 0.06, S * 0.3, S * 0.1, S * 0.42)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = `rgba(255,225,120,${0.9 * fl})`
  ctx.beginPath()
  ctx.ellipse(S * 0.02, S * 0.4, S * 0.045, S * 0.07, 0, 0, TAU)
  ctx.fill()
  // skull totem
  ctx.strokeStyle = '#4c3a26'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(0, S * 0.1); ctx.lineTo(0, -S * 0.62)
  ctx.stroke()
  ctx.strokeStyle = '#40301e'
  ctx.lineWidth = 1.2
  ctx.beginPath()
  ctx.moveTo(-S * 0.14, -S * 0.4); ctx.lineTo(S * 0.14, -S * 0.44)
  ctx.stroke()
  ctx.fillStyle = '#ddd6c4' // skull
  ctx.beginPath()
  ctx.arc(0, -S * 0.66, S * 0.11, 0, TAU)
  ctx.fill()
  box(ctx, -S * 0.055, -S * 0.6, S * 0.11, S * 0.05, '#ddd6c4')
  ctx.fillStyle = '#1a1512'
  ctx.beginPath(); ctx.arc(-S * 0.04, -S * 0.67, S * 0.028, 0, TAU); ctx.fill()
  ctx.beginPath(); ctx.arc(S * 0.04, -S * 0.67, S * 0.028, 0, TAU); ctx.fill()
  // tattered red rag
  ctx.fillStyle = '#7e2a22'
  ctx.beginPath()
  ctx.moveTo(0, -S * 0.52)
  ctx.lineTo(S * 0.18, -S * 0.48 + Math.sin(now * 0.003) * 1.2)
  ctx.lineTo(S * 0.04, -S * 0.4)
  ctx.closePath()
  ctx.fill()
  void rand
}

// ---- construction sites -------------------------------------------------------------

function drawSite(ctx: CanvasRenderingContext2D, b: Building, color: string, now: number, groundSquash = 1, prep = false): void {
  const def = BUILDINGS[b.type]
  // SITE PREP: the crew is still clearing trees / levelling the ground — the land
  // underneath is untouched and stays fully visible; only a see-through image of the
  // planned building marks the works. Scaffold, dirt pad and piles wait for the
  // blueprint stage (prep false), so nothing on the tile is erased by the click.
  if (prep && !isBoxOrder(b.type)) {
    if (b.type === 'road') {
      // A road has no art to preview (its finished look IS the terrain) — mark the
      // clearing works with survey stakes (mirrors the 3D stakes) instead of the
      // dirt-pad/gravel earthworks, which would contradict the untouched land.
      for (const [sx, sy] of [[-7, -4], [7, -5], [-5, 5], [8, 4]] as const) {
        ctx.strokeStyle = '#5f4a2c'
        ctx.lineWidth = 1.6
        ctx.beginPath()
        ctx.moveTo(sx, sy + 3)
        ctx.lineTo(sx, sy - 4)
        ctx.stroke()
        ctx.fillStyle = '#e6dec6'
        ctx.fillRect(sx - 1.8, sy - 6.2, 3.6, 2.4)
      }
      return
    }
    ctx.save()
    ctx.globalAlpha *= 0.38
    drawBuildingArt(ctx, b.type, color, now, b.id, b.type === 'pier' || b.type === 'ramp' ? pierArtAngle(b.dir) : 0, groundSquash, b.hallPhase)
    ctx.restore()
    return
  }
  // A chop order isn't a building rising — it's a tree being felled. Draw the
  // felling marker with a progress ring and skip the scaffold/material visuals.
  if (isBoxOrder(b.type)) {
    // Box orders (chop + terraform) are just a low ground marker — for chop the tree
    // keeps standing (terrain decoration pass) above it. No dark pad/shadow; a floating
    // status badge is the real indicator, and a slim progress arc appears only once a
    // serf is actually working it, so idle queued orders stay uncluttered.
    const cpct = Math.max(0, Math.min(1, b.progress / Math.max(1, siteBuildTime(b.type, b.q, b.r))))
    if (b.type === 'chop') bChop(ctx)
    else bTerra(ctx, b.type)
    if (cpct > 0.02) {
      ctx.strokeStyle = 'rgba(16,20,16,0.5)'
      ctx.lineWidth = 2.6
      ctx.beginPath(); ctx.arc(0, S * 0.15, S * 0.42, 0, TAU); ctx.stroke()
      ctx.strokeStyle = color
      ctx.lineWidth = 2.2
      ctx.beginPath(); ctx.arc(0, S * 0.15, S * 0.42, -Math.PI / 2, -Math.PI / 2 + TAU * cpct); ctx.stroke()
    }
    return
  }
  const pct = Math.max(0, Math.min(1, b.progress / Math.max(1, def.buildTime)))
  shadow(ctx, S * 0.55, S * 0.24, S * 0.4)
  // dirt pad
  ctx.fillStyle = 'rgba(112,88,58,0.75)'
  ctx.beginPath()
  ctx.ellipse(0, S * 0.16, S * 0.55, S * 0.32, 0, 0, TAU)
  ctx.fill()
  // material piles
  box(ctx, -S * 0.44, S * 0.14, S * 0.2, S * 0.12, '#a5865c')
  box(ctx, -S * 0.4, S * 0.05, S * 0.16, S * 0.1, '#8d8578')
  ctx.strokeStyle = '#7a5c38' // stacked beams
  ctx.lineWidth = 2
  for (let i = 0; i < 3; i++) {
    ctx.beginPath()
    ctx.moveTo(S * 0.2, S * 0.3 - i * 2.4)
    ctx.lineTo(S * 0.52, S * 0.24 - i * 2.4)
    ctx.stroke()
  }
  // rising frame of the future building (grows with progress)
  if (pct > 0.15) {
    ctx.save()
    ctx.globalAlpha = 0.45 + pct * 0.4
    ctx.beginPath()
    ctx.rect(-S, S * 0.5 - S * 1.6 * pct, S * 2, S * 1.6 * pct)
    ctx.clip()
    // Oriented builds keep their aim through the rising frames too (pier AND ramp —
    // else the art snaps to angle 0 mid-build and back on completion).
    drawBuildingArt(ctx, b.type === 'road' ? 'road' : b.type, color, now, b.id, b.type === 'pier' || b.type === 'ramp' ? pierArtAngle(b.dir) : 0, groundSquash, b.hallPhase)
    ctx.restore()
  }
  if (b.type === 'road') { // road sites: packed gravel appearing
    ctx.fillStyle = `rgba(165,134,92,${0.3 + 0.6 * pct})`
    ctx.beginPath()
    ctx.ellipse(0, S * 0.05, S * 0.4, S * 0.24, 0, 0, TAU)
    ctx.fill()
  }
  // scaffolding
  ctx.strokeStyle = '#b08d58'
  ctx.lineWidth = 1.3
  ctx.beginPath()
  ctx.moveTo(-S * 0.5, S * 0.34); ctx.lineTo(-S * 0.5, -S * 0.5)
  ctx.moveTo(S * 0.5, S * 0.34); ctx.lineTo(S * 0.5, -S * 0.5)
  ctx.moveTo(-S * 0.56, -S * 0.42); ctx.lineTo(S * 0.56, -S * 0.42)
  ctx.moveTo(-S * 0.56, -S * 0.05); ctx.lineTo(S * 0.56, -S * 0.05)
  ctx.moveTo(-S * 0.5, S * 0.3); ctx.lineTo(S * 0.5, -S * 0.42)
  ctx.stroke()
  // radial progress ring
  ctx.strokeStyle = 'rgba(16,20,16,0.6)'
  ctx.lineWidth = 3.2
  ctx.beginPath()
  ctx.arc(0, 0, S * 0.72, 0, TAU)
  ctx.stroke()
  ctx.strokeStyle = color
  ctx.lineWidth = 2.2
  ctx.beginPath()
  ctx.arc(0, 0, S * 0.72, -Math.PI / 2, -Math.PI / 2 + TAU * pct)
  ctx.stroke()
}

/** HP bar / damage smoke for finished buildings. Call after drawBuilding. */
export function drawBuildingOverlay(ctx: CanvasRenderingContext2D, b: Building, now: number): void {
  if (!b.done || b.type === 'plant') return
  const max = buildingMaxHp(b)
  if (b.hp < max - 0.5) {
    drawHpBar(ctx, -S * 1.02, b.hp / max, S * 1.1)
    if (b.hp < max * 0.5) { // smoke wisps when badly hurt
      const t = now * 0.001
      for (let i = 0; i < 2; i++) {
        const ph = (t * 0.5 + i * 0.5) % 1
        ctx.fillStyle = `rgba(60,58,55,${0.35 * (1 - ph)})`
        ctx.beginPath()
        ctx.arc(Math.sin((t + i * 3) * 2) * S * 0.15 + i * S * 0.2 - S * 0.1, -S * 0.5 - ph * S * 0.7, S * (0.08 + ph * 0.14), 0, TAU)
        ctx.fill()
      }
    }
  }
}

/** Rally-point flag (draw at the rally hex, world-translated). */
export function drawRallyFlag(ctx: CanvasRenderingContext2D, color: string, now: number): void {
  shadow(ctx, S * 0.14, S * 0.06, S * 0.1)
  flag(ctx, 0, S * 0.1, S * 0.55, color, Math.sin(now * 0.004) * 1.2)
}

/** Ghost preview for buildMode: tinted validity hex + translucent art. */
/** `cells` are footprint-tile pixel offsets relative to the current origin (the hover
 *  hex). Omitted = a single hex at the origin. Multi-hex ghosts (the town hall) tint
 *  every occupied hex and place the art on their centroid. `angle` rotates oriented
 *  art (the pier) to the previewed facing. */
export function drawGhost(
  ctx: CanvasRenderingContext2D,
  type: BuildingType,
  color: string,
  valid: boolean,
  now: number,
  cells?: { x: number; y: number }[],
  angle = 0,
  cellOk?: boolean[],
): void {
  ctx.save()
  ctx.lineWidth = 1.6
  const pts = cells && cells.length ? cells : [{ x: 0, y: 0 }]
  // Per-cell colouring (the town hall passes majority-green / minority-red per hex);
  // without it every cell follows the single `valid` flag.
  for (let i = 0; i < pts.length; i++) {
    const ok = cellOk ? cellOk[i] !== false : valid
    ctx.fillStyle = ok ? 'rgba(90,220,110,0.2)' : 'rgba(230,70,60,0.28)'
    ctx.strokeStyle = ok ? 'rgba(120,255,150,0.85)' : 'rgba(255,90,80,0.95)'
    ctx.beginPath()
    hexPathAt(ctx, pts[i].x, pts[i].y, S * 0.94)
    ctx.fill()
    ctx.stroke()
  }
  ctx.globalAlpha = 0.55
  if (type === 'road') {
    ctx.strokeStyle = '#a5865c'
    ctx.lineWidth = S * 0.4
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(-S * 0.5, 0)
    ctx.lineTo(S * 0.5, 0)
    ctx.stroke()
  } else {
    let ax = 0
    let ay = 0
    for (const c of pts) {
      ax += c.x
      ay += c.y
    }
    ctx.translate(ax / pts.length, ay / pts.length)
    drawBuildingArt(ctx, type, color, now, 1, angle)
  }
  ctx.restore()
}

// ---- units ------------------------------------------------------------------------------

const UR = S * 0.2 // unit body radius
const SKIN = '#e8c39a'

function clothColor(color: string): { main: RGB; dark: RGB } {
  const c = rgb(color)
  return { main: mixRgb(c, [40, 36, 32], 0.15), dark: shade(c, 0.6) }
}

/**
 * Draw one unit; ctx translated to its (smoothed) world position.
 * bob: vertical walk-cycle offset already computed by the renderer.
 */
export function drawUnit(ctx: CanvasRenderingContext2D, u: UnitWire, color: string, now: number, bob: number): void {
  shadow(ctx, UR * 1.1, UR * 0.5, UR * 1.05)
  ctx.save()
  ctx.translate(0, bob)
  switch (u.type) {
    case 'wolf': drawWolf(ctx); break
    case 'bear': drawBear(ctx); break
    case 'deer': drawDeer(ctx); break
    default: drawHumanoid(ctx, u, color, now)
  }
  ctx.restore()
  const maxHp = unitMaxHp(u)
  if (u.hp < maxHp - 0.01) drawHpBar(ctx, -UR * 2.6, u.hp / maxHp, UR * 3)
}

/** What silhouette a citizen wears, from the real item in their hands right now. */
export function citizenLook(u: UnitWire): string {
  // The hall crafter hauling material from the annex to the bench carries a
  // cosmetic bundle even while nominally 'toJob' — show the carry pose.
  if (u.craftHaul) return 'carrier'
  const carrying = !!u.carrying && Object.values(u.carrying).some((v) => (v ?? 0) > 0)
  // The carry pose belongs to logistics legs only — a builder or miner with
  // leftover goods in the satchel still works with their tool in hand.
  const atWork =
    u.state === 'working' || u.state === 'building' || u.state === 'fighting' || u.state === 'toJob'
  if (carrying && !atWork) return 'carrier'
  switch (u.person?.held) {
    case 'bow': return 'archer'
    case 'spear': return 'spearman'
    case 'sword': return 'swordsman'
    // Each tool gets its own silhouette so a hoe never looks like a hammer.
    case 'hammer': return 'hammer'
    case 'smithhammer': return 'hammer'
    case 'rod': return 'rod'
    case 'hoe': return 'hoe'
    case 'shovel': return 'shovel'
    case 'axe': return 'axe'
    case 'pick': return 'pick'
    default: return 'idle' // empty hands — a plain villager
  }
}

/** A labourer's wide straw hat — the shared marker for any tool-carrying serf. */
function strawHat(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = '#d9b45e'
  ctx.beginPath()
  ctx.ellipse(0, -UR * 1.2, UR * 0.85, UR * 0.32, 0, 0, TAU)
  ctx.fill()
  ctx.beginPath()
  ctx.arc(0, -UR * 1.3, UR * 0.42, Math.PI, 0)
  ctx.fill()
}

/** A tool in the serf's right hand, each with its own head so hammer/hoe/axe/pick
 *  read as clearly different implements. Swings while working. */
function toolInHand(ctx: CanvasRenderingContext2D, tool: string, working: boolean, swing: number): void {
  const handle = '#7a5c38'
  const steel = '#9aa1a8'
  const woodHead = '#a8845a' // lighter carved wood so heads read against the haft
  ctx.save()
  ctx.translate(UR * 0.9, -UR * 0.2)
  if (working) ctx.rotate(swing * 0.7)
  ctx.strokeStyle = handle
  ctx.lineWidth = 1.1
  switch (tool) {
    case 'hammer': {
      ctx.beginPath(); ctx.moveTo(0, UR * 0.6); ctx.lineTo(0, -UR * 0.9); ctx.stroke()
      ctx.fillStyle = woodHead // carved mallet head (wood-only tool)
      ctx.fillRect(-UR * 0.4, -UR * 1.2, UR * 0.8, UR * 0.42) // blocky head
      break
    }
    case 'rod': {
      // slender pole angled out with a line down to the water
      ctx.beginPath(); ctx.moveTo(0, UR * 0.6); ctx.lineTo(UR * 0.7, -UR * 1.5); ctx.stroke()
      ctx.strokeStyle = '#d8d2c2'
      ctx.lineWidth = 0.6
      ctx.beginPath(); ctx.moveTo(UR * 0.7, -UR * 1.5); ctx.quadraticCurveTo(UR * 1.05, -UR * 0.6, UR * 0.95, UR * 0.5); ctx.stroke()
      break
    }
    case 'hoe': {
      ctx.beginPath(); ctx.moveTo(0, UR * 0.6); ctx.lineTo(0, -UR * 0.95); ctx.stroke()
      ctx.fillStyle = woodHead // carved blade jutting forward (wood-only tool)
      ctx.beginPath()
      ctx.moveTo(0, -UR * 0.95)
      ctx.lineTo(UR * 0.64, -UR * 0.74)
      ctx.lineTo(UR * 0.56, -UR * 0.44)
      ctx.lineTo(-UR * 0.06, -UR * 0.72)
      ctx.closePath(); ctx.fill()
      break
    }
    case 'shovel': {
      ctx.beginPath(); ctx.moveTo(0, UR * 0.6); ctx.lineTo(0, -UR * 0.9); ctx.stroke()
      ctx.fillStyle = woodHead // carved spade blade (wood-only tool)
      ctx.beginPath()
      ctx.moveTo(-UR * 0.22, -UR * 0.9)
      ctx.lineTo(UR * 0.22, -UR * 0.9)
      ctx.lineTo(UR * 0.18, -UR * 1.3)
      ctx.quadraticCurveTo(0, -UR * 1.45, -UR * 0.18, -UR * 1.3)
      ctx.closePath(); ctx.fill()
      break
    }
    case 'axe': {
      ctx.beginPath(); ctx.moveTo(0, UR * 0.6); ctx.lineTo(0, -UR * 1.0); ctx.stroke()
      ctx.fillStyle = woodHead // carved wooden wedge (wood-only tool)
      ctx.beginPath()
      ctx.moveTo(0, -UR * 1.02)
      ctx.quadraticCurveTo(UR * 0.72, -UR * 0.86, UR * 0.6, -UR * 0.48)
      ctx.quadraticCurveTo(UR * 0.34, -UR * 0.52, 0, -UR * 0.64)
      ctx.closePath(); ctx.fill()
      break
    }
    case 'pick': {
      ctx.beginPath(); ctx.moveTo(0, UR * 0.6); ctx.lineTo(0, -UR * 0.82); ctx.stroke()
      ctx.strokeStyle = woodHead // carved wooden crescent (wood-only tool)
      ctx.lineWidth = UR * 0.24
      ctx.lineCap = 'round'
      ctx.beginPath()
      ctx.moveTo(-UR * 0.62, -UR * 0.5)
      ctx.quadraticCurveTo(0, -UR * 1.16, UR * 0.62, -UR * 0.5)
      ctx.stroke()
      ctx.lineCap = 'butt'
      break
    }
  }
  ctx.restore()
}

function drawHumanoid(ctx: CanvasRenderingContext2D, u: UnitWire, color: string, now: number): void {
  const cc = clothColor(color)
  const raider = u.type === 'raider'
  // body: cloth teardrop
  ctx.fillStyle = raider ? '#3a3531' : css(cc.main)
  ctx.beginPath()
  ctx.moveTo(-UR, UR * 0.9)
  ctx.quadraticCurveTo(-UR * 1.05, -UR * 0.4, 0, -UR * 0.7)
  ctx.quadraticCurveTo(UR * 1.05, -UR * 0.4, UR, UR * 0.9)
  ctx.closePath()
  ctx.fill()
  ctx.fillStyle = raider ? '#26221f' : css(cc.dark) // belt shade
  ctx.fillRect(-UR * 0.85, UR * 0.25, UR * 1.7, UR * 0.28)
  // head
  ctx.fillStyle = raider ? '#2c2724' : SKIN
  ctx.beginPath()
  ctx.arc(0, -UR * 1.05, UR * 0.62, 0, TAU)
  ctx.fill()
  const swing = Math.sin(now * 0.008 + u.id * 1.7)
  const working = u.state === 'working' || u.state === 'building'
  // Citizens have no fixed class — their look follows what they're holding.
  const look = u.type === 'citizen' ? citizenLook(u) : u.type
  switch (look) {
    case 'worker': // legacy NPC labourer — draw a hammer
    case 'hammer':
    case 'hoe':
    case 'axe':
    case 'rod':
    case 'pick': {
      strawHat(ctx)
      toolInHand(ctx, look === 'worker' ? 'hammer' : look, working, swing)
      break
    }
    case 'carrier': {
      // hood
      ctx.fillStyle = css(cc.dark)
      ctx.beginPath()
      ctx.arc(0, -UR * 1.15, UR * 0.55, Math.PI * 0.95, Math.PI * 2.05)
      ctx.fill()
      const carrying = !!u.craftHaul || (!!u.carrying && Object.values(u.carrying).some((v) => (v ?? 0) > 0))
      if (carrying) { // visible crate on the back/head
        ctx.fillStyle = '#b08d58'
        ctx.fillRect(-UR * 0.75, -UR * 2.35, UR * 1.5, UR * 1.1)
        ctx.strokeStyle = '#7c5f36'
        ctx.lineWidth = 0.8
        ctx.strokeRect(-UR * 0.75, -UR * 2.35, UR * 1.5, UR * 1.1)
        ctx.beginPath()
        ctx.moveTo(-UR * 0.75, -UR * 1.8); ctx.lineTo(UR * 0.75, -UR * 1.8)
        ctx.stroke()
      }
      break
    }
    case 'spearman': {
      // helm
      ctx.fillStyle = '#9aa1a8'
      ctx.beginPath()
      ctx.arc(0, -UR * 1.15, UR * 0.58, Math.PI, 0)
      ctx.fill()
      // spear
      ctx.strokeStyle = '#8a6b42'
      ctx.lineWidth = 1.1
      ctx.beginPath()
      ctx.moveTo(UR * 0.85, UR * 0.9)
      ctx.lineTo(UR * 1.15, -UR * 2.1)
      ctx.stroke()
      ctx.fillStyle = '#a8845a' // fire-hardened wooden point (wood-tier weapon)
      ctx.beginPath()
      ctx.moveTo(UR * 1.15, -UR * 2.1)
      ctx.lineTo(UR * 0.95, -UR * 1.65)
      ctx.lineTo(UR * 1.35, -UR * 1.7)
      ctx.closePath()
      ctx.fill()
      break
    }
    case 'archer': {
      // hood
      ctx.fillStyle = css(cc.dark)
      ctx.beginPath()
      ctx.arc(0, -UR * 1.15, UR * 0.58, Math.PI * 0.9, Math.PI * 2.1)
      ctx.fill()
      // bow + string
      ctx.strokeStyle = '#7a5330'
      ctx.lineWidth = 1.2
      ctx.beginPath()
      ctx.arc(UR * 0.9, -UR * 0.3, UR * 1.05, -Math.PI * 0.42, Math.PI * 0.42)
      ctx.stroke()
      ctx.strokeStyle = 'rgba(230,225,210,0.9)'
      ctx.lineWidth = 0.6
      ctx.beginPath()
      ctx.moveTo(UR * 0.9 + Math.cos(-Math.PI * 0.42) * UR * 1.05, -UR * 0.3 + Math.sin(-Math.PI * 0.42) * UR * 1.05)
      ctx.lineTo(UR * 0.9 + Math.cos(Math.PI * 0.42) * UR * 1.05, -UR * 0.3 + Math.sin(Math.PI * 0.42) * UR * 1.05)
      ctx.stroke()
      // quiver
      ctx.fillStyle = '#6e4f2c'
      ctx.save()
      ctx.translate(-UR * 0.8, -UR * 0.9)
      ctx.rotate(-0.5)
      ctx.fillRect(-UR * 0.18, -UR * 0.5, UR * 0.36, UR * 1.0)
      ctx.strokeStyle = '#d8d2c2'
      ctx.lineWidth = 0.7
      ctx.beginPath()
      ctx.moveTo(-UR * 0.06, -UR * 0.5); ctx.lineTo(-UR * 0.06, -UR * 0.85)
      ctx.moveTo(UR * 0.08, -UR * 0.5); ctx.lineTo(UR * 0.08, -UR * 0.8)
      ctx.stroke()
      ctx.restore()
      break
    }
    case 'raider': {
      // deep dark hood with glinting eyes
      ctx.fillStyle = '#1c1815'
      ctx.beginPath()
      ctx.arc(0, -UR * 1.05, UR * 0.68, 0, TAU)
      ctx.fill()
      ctx.fillStyle = '#e3d18f'
      ctx.beginPath(); ctx.arc(-UR * 0.2, -UR * 1.05, UR * 0.09, 0, TAU); ctx.fill()
      ctx.beginPath(); ctx.arc(UR * 0.2, -UR * 1.05, UR * 0.09, 0, TAU); ctx.fill()
      // jagged blade
      ctx.fillStyle = '#8e969c'
      ctx.beginPath()
      ctx.moveTo(UR * 0.8, UR * 0.4)
      ctx.lineTo(UR * 1.7, -UR * 0.9)
      ctx.lineTo(UR * 1.35, -UR * 0.55)
      ctx.lineTo(UR * 1.5, -UR * 0.15)
      ctx.closePath()
      ctx.fill()
      // blood-red sash
      ctx.strokeStyle = '#7e2a22'
      ctx.lineWidth = UR * 0.24
      ctx.beginPath()
      ctx.moveTo(-UR * 0.7, -UR * 0.45)
      ctx.lineTo(UR * 0.7, UR * 0.35)
      ctx.stroke()
      break
    }
    case 'swordsman': {
      // helm
      ctx.fillStyle = '#9aa1a8'
      ctx.beginPath()
      ctx.arc(0, -UR * 1.15, UR * 0.58, Math.PI, 0)
      ctx.fill()
      // sword: gold guard + steel blade, raised while fighting
      ctx.save()
      ctx.translate(UR * 0.95, UR * 0.1)
      ctx.rotate(working ? swing * 0.4 : -0.3)
      ctx.strokeStyle = '#caa24e'
      ctx.lineWidth = 1.4
      ctx.beginPath(); ctx.moveTo(-UR * 0.35, UR * 0.35); ctx.lineTo(UR * 0.35, UR * 0.35); ctx.stroke()
      ctx.fillStyle = '#cdd3d8'
      ctx.beginPath()
      ctx.moveTo(-UR * 0.12, UR * 0.3)
      ctx.lineTo(UR * 0.12, UR * 0.3)
      ctx.lineTo(0, -UR * 1.25)
      ctx.closePath()
      ctx.fill()
      ctx.restore()
      break
    }
    // 'fists' (unarmed) and any other look: just the plain body already drawn.
  }
}

function drawWolf(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = '#83878e'
  ctx.beginPath()
  ctx.ellipse(0, 0, UR * 1.25, UR * 0.68, 0, 0, TAU) // body
  ctx.fill()
  ctx.beginPath()
  ctx.arc(UR * 1.1, -UR * 0.35, UR * 0.5, 0, TAU) // head
  ctx.fill()
  ctx.beginPath() // muzzle
  ctx.moveTo(UR * 1.4, -UR * 0.4)
  ctx.lineTo(UR * 1.85, -UR * 0.22)
  ctx.lineTo(UR * 1.4, -UR * 0.08)
  ctx.closePath()
  ctx.fill()
  // ears
  ctx.fillStyle = '#6b6f76'
  ctx.beginPath()
  ctx.moveTo(UR * 0.85, -UR * 0.75); ctx.lineTo(UR * 0.95, -UR * 1.25); ctx.lineTo(UR * 1.15, -UR * 0.8)
  ctx.closePath(); ctx.fill()
  // tail
  ctx.strokeStyle = '#6b6f76'
  ctx.lineWidth = UR * 0.35
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.moveTo(-UR * 1.15, -UR * 0.1)
  ctx.quadraticCurveTo(-UR * 1.7, -UR * 0.5, -UR * 1.85, -UR * 1.0)
  ctx.stroke()
  // eye
  ctx.fillStyle = '#e8c84a'
  ctx.beginPath()
  ctx.arc(UR * 1.15, -UR * 0.45, UR * 0.09, 0, TAU)
  ctx.fill()
  // pale belly
  ctx.fillStyle = 'rgba(200,204,210,0.5)'
  ctx.beginPath()
  ctx.ellipse(0, UR * 0.3, UR * 0.9, UR * 0.28, 0, 0, TAU)
  ctx.fill()
}

function drawBear(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = '#77522f'
  ctx.beginPath()
  ctx.ellipse(0, -UR * 0.15, UR * 1.5, UR * 1.1, 0, 0, TAU) // big body
  ctx.fill()
  ctx.beginPath()
  ctx.arc(UR * 1.2, -UR * 0.7, UR * 0.65, 0, TAU) // head
  ctx.fill()
  // ears
  ctx.beginPath(); ctx.arc(UR * 0.9, -UR * 1.3, UR * 0.22, 0, TAU); ctx.fill()
  ctx.beginPath(); ctx.arc(UR * 1.5, -UR * 1.3, UR * 0.22, 0, TAU); ctx.fill()
  // muzzle
  ctx.fillStyle = '#a07a4e'
  ctx.beginPath()
  ctx.ellipse(UR * 1.55, -UR * 0.55, UR * 0.32, UR * 0.24, 0, 0, TAU)
  ctx.fill()
  ctx.fillStyle = '#2a1e12'
  ctx.beginPath()
  ctx.arc(UR * 1.72, -UR * 0.6, UR * 0.1, 0, TAU)
  ctx.fill()
  // shoulder hump highlight + claws
  ctx.fillStyle = 'rgba(150,110,66,0.5)'
  ctx.beginPath()
  ctx.ellipse(-UR * 0.3, -UR * 0.8, UR * 0.7, UR * 0.35, -0.2, 0, TAU)
  ctx.fill()
  ctx.strokeStyle = '#d8cfc0'
  ctx.lineWidth = 0.8
  for (let i = 0; i < 3; i++) {
    ctx.beginPath()
    ctx.moveTo(UR * (0.5 + i * 0.2), UR * 0.85)
    ctx.lineTo(UR * (0.55 + i * 0.2), UR * 1.05)
    ctx.stroke()
  }
}

function drawDeer(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = '#c9a76f'
  ctx.beginPath()
  ctx.ellipse(0, 0, UR * 1.05, UR * 0.55, 0, 0, TAU) // slim body
  ctx.fill()
  // neck + head
  ctx.beginPath()
  ctx.moveTo(UR * 0.7, -UR * 0.1)
  ctx.lineTo(UR * 1.15, -UR * 1.1)
  ctx.lineTo(UR * 1.45, -UR * 1.05)
  ctx.lineTo(UR * 1.0, UR * 0.2)
  ctx.closePath()
  ctx.fill()
  ctx.beginPath()
  ctx.ellipse(UR * 1.32, -UR * 1.12, UR * 0.32, UR * 0.22, -0.35, 0, TAU)
  ctx.fill()
  // antlers
  ctx.strokeStyle = '#8a6b42'
  ctx.lineWidth = 0.9
  ctx.beginPath()
  ctx.moveTo(UR * 1.2, -UR * 1.3)
  ctx.lineTo(UR * 1.0, -UR * 1.9)
  ctx.moveTo(UR * 1.08, -UR * 1.65)
  ctx.lineTo(UR * 0.85, -UR * 1.85)
  ctx.moveTo(UR * 1.35, -UR * 1.32)
  ctx.lineTo(UR * 1.5, -UR * 1.95)
  ctx.moveTo(UR * 1.44, -UR * 1.7)
  ctx.lineTo(UR * 1.65, -UR * 1.9)
  ctx.stroke()
  // white rump + eye
  ctx.fillStyle = '#f0e8d8'
  ctx.beginPath()
  ctx.arc(-UR * 0.95, -UR * 0.1, UR * 0.24, 0, TAU)
  ctx.fill()
  ctx.fillStyle = '#231a10'
  ctx.beginPath()
  ctx.arc(UR * 1.38, -UR * 1.16, UR * 0.07, 0, TAU)
  ctx.fill()
}
